import React from "react";
import type { ApolloClient } from "@apollo/client";
import queryString from "query-string";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { push } from "store/actions/router";
import { toast } from "react-toastify";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Form } from "react-bootstrap";
import { processedStatuses } from "statics/payroll";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants, payrollConstants } from "actions/types";
import { doesBankNeedReAuth } from "utils/bankReAuth";
import {
  filter,
  find,
  get,
  groupBy,
  includes,
  isEmpty,
  keyBy,
  map,
  size,
  some,
} from "lodash";
import {
  invalidBankIdsLinkedToGroupsSelector,
  banksSelector,
  isBankPending,
} from "store/selectors/bank";
import { FiCalendar, FiZap, FiPause } from "react-icons/fi";
import {
  cancelPayroll,
  dismissPayroll,
  recheckPayrollBalance,
} from "actions/payrollActions";
import {
  currentPayrolls,
  getAllEmployerGroups,
  getLinkedEmployees,
} from "actions/employerActions";
import {
  hasEnabled360PayrollIntegration,
  hasEnabledPayrollIntegration,
} from "store/selectors/employer";

import DismissPayrollModal from "./DismissPayrollModal";
import IconTable from "components/IconTable";
import IconSpinner from "components/IconSpinner";
import PayrollStatus from "components/PayrollStatus";
import Button from "components/Button";
import Alert from "components/Alert";

import "./ProcessGroupsTable.scss";

import type { RootState } from "types/state";

const commonDateFormat = "MMM Do YYYY";

const allGroups = {
  id: "",
  value: "ALL_GROUPS",
  name: "All Groups",
};

const actions = [
  employerConstants.PROCESS_PAYROLL,
  employerConstants.GET_LINKED_EMPLOYEES,
  employerConstants.GET_EMPLOYER_GROUPS,
  employerConstants.CURRENT_PAYROLLS,
  payrollConstants.CANCEL_PAYROLL,
  payrollConstants.DISMISS_PAYROLL,
];

const isLoadingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  const linkedEmployees = state.employer.linkedEmployees;
  const employerLinkRequests = filter(
    state.employer.employerLinkRequests,
    (request) => (request as { status?: string }).status === "REQUEST_ACCESS"
  );

  const linkedEmployeesByGroup = groupBy(
    linkedEmployees,
    "groupWithSchedule.group.id"
  );

  const employerLinkRequestsByGroup = groupBy(employerLinkRequests, "group.id");

  // only show groups that have linkRequests or linkedEmployees
  const groupsToShow = filter(state.employer.groups, (group) => {
    const hasLinkRequest = !isEmpty(
      employerLinkRequestsByGroup[group.id as string]
    );
    const hasLinkedEmployees = !isEmpty(
      linkedEmployeesByGroup[group.id as string]
    );
    return hasLinkRequest || hasLinkedEmployees;
  });
  const groupsById = keyBy(groupsToShow, "id");

  // only show payrolls that are associated with groups that have linkRequests or linkedEmployees

  const payrollsToShow = filter(state.employer.currentPayrolls, (payroll) => {
    return Boolean(groupsById[payroll.groupId as string]);
  });

  return {
    groupsById,
    groups: groupsToShow,
    linkedEmployeesByGroup,
    employerLinkRequestsByGroup,
    error: errorSelector(state),
    isFetching: isLoadingSelector(state),
    invalidBankIdsLinkedToGroups: invalidBankIdsLinkedToGroupsSelector(state),
    has360PayrollIntegration: hasEnabled360PayrollIntegration(state),
    hasPayrollIntegration: hasEnabledPayrollIntegration(state),
    payrolls: payrollsToShow,
    bankAccounts: banksSelector(state),
    banksNeedingReAuth: banksSelector(state),
  };
};

const mapDispatchToProps = {
  push,
  getLinkedEmployees,
  getAllEmployerGroups,
  cancelPayroll,
  recheckPayrollBalance,
  currentPayrolls,
  dismissPayroll,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onSubmit?: (...args: unknown[]) => void;
  onClose?: (...args: unknown[]) => void;
  groupId?: string;
  onClick?: (...args: unknown[]) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  // initialized to `true`; never read or re-written
  initialFetching: boolean;
  // initialized to `false`; never read or re-written
  showConfirmationModal: boolean;
  // initialized to `null`; never read or re-written
  pendingCancellationId: string | null;
  showDismissModal: boolean;
  // initialized to `false`; never read or re-written
  showOneTimePayroll: boolean;
  payrollData: Record<string, unknown> | null;
  selectedPayrollGroup: string;
  payPeriodId: string;
}

// A payroll row as supplied by IconTable's customComponent callback. The
// store types payrolls as `Record<string, unknown>`, so each accessed field
// is narrowed/asserted at the use site.
type PayrollRow = Record<string, unknown>;

// Options rendered in the group <select>: the static "All Groups" entry has an
// id; the entries mapped from props.groups do not (id stays undefined, as in
// the original JS), so id is optional.
interface PayrollGroupFilterOption {
  id?: string;
  value: unknown;
  name: unknown;
}

export class PayrollTable extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const payPeriodId = queryString.parse(window.location.search).payPeriodId;

    this.state = {
      initialFetching: true,
      showConfirmationModal: false,
      pendingCancellationId: null,
      showDismissModal: false,
      showOneTimePayroll: false,
      payrollData: null,
      selectedPayrollGroup: "ALL_GROUPS",
      payPeriodId: (payPeriodId as string) || "",
    };
  }

  componentDidMount() {
    // @ts-expect-error pre-existing bug: currentPayrolls requires (client,
    // companyId) but is called with client only (companyId === undefined);
    // preserved as-is per conversion rules.
    void this.props.currentPayrolls(this.props.client as ApolloClient<object>);
  }

  _getBankAccountInfo = (groupId: string) => {
    const group = this.props.groupsById[groupId];
    const bankAccountId = get(group, "associatedBankAccount.id") as
      | string
      | undefined;

    const banksNeedingReAuth = this.props.banksNeedingReAuth || [];
    const needsReAuth = doesBankNeedReAuth(
      // UserBank.loginRequired is `boolean | null` vs the util's `boolean?`;
      // cast at the boundary to the util's parameter type.
      banksNeedingReAuth as Parameters<typeof doesBankNeedReAuth>[0],
      bankAccountId
    );
    return { bankAccountId, needsReAuth };
  };

  _payrollGroupFilter = (): PayrollGroupFilterOption[] => {
    const groups = this.props.groups.map((group) => {
      return { value: group.name, name: group.name };
    });
    return [allGroups, ...groups];
  };

  _filteredPayrollGroups = () => {
    const allGroups = this.props.payrolls;
    if (this.state.selectedPayrollGroup === "ALL_GROUPS") return allGroups;

    return allGroups.filter((payroll) => {
      const groupName = get(
        this.props.groupsById[payroll.groupId as string],
        "name"
      );
      return groupName === this.state.selectedPayrollGroup;
    });
  };

  _buildColumns = () => {
    const columns = [
      {
        label: "Group Name",
        customComponent: (props: PayrollRow) => {
          const groupName = get(
            this.props.groupsById[props.groupId as string],
            "name"
          );
          return groupName as React.ReactNode;
        },
      },
      {
        label: "Payroll Period",
        customComponent: (props: PayrollRow) => {
          const start = props.payPeriodStart;
          const end = props.payPeriodEnd;
          const period = `${moment(start as moment.MomentInput).format(
            commonDateFormat
          )} - ${moment(end as moment.MomentInput).format(commonDateFormat)}`;
          return period;
        },
      },
      {
        label: "Processing Date",
        customComponent: (props: PayrollRow) => {
          const payrollProcessingDate = props.payrollProcessingDate;
          return moment(payrollProcessingDate as moment.MomentInput).format(
            commonDateFormat
          );
        },
      },
      {
        label: "Users",
        customComponent: (props: PayrollRow) => {
          const groupId = props.groupId as string;
          const linkedEmployees = get(
            this.props.linkedEmployeesByGroup,
            groupId,
            []
          );
          return props.payrollType === "ADHOC"
            ? (props.userCount as React.ReactNode)
            : size(linkedEmployees);
        },
      },
      {
        label: "Type",
        customComponent: (props: PayrollRow) => {
          const groupId = props.groupId as string;
          const { needsReAuth } = this._getBankAccountInfo(groupId);

          if (props.payrollType === "ADHOC") {
            return (
              <div className="payroll-type">
                <span className="payroll-icon">
                  <FiZap
                    size={"12px"}
                    color={"#FFFFFF"}
                    fill={"#FFFFFF"}
                    stroke={"#009baa"}
                    strokeWidth={"2"}
                  />
                </span>
                <p className="payroll-label-one-time">One-Time</p>
              </div>
            );
          }
          return (
            <div className="payroll-type">
              {needsReAuth ? (
                <>
                  <span className="payroll-icon">
                    <FiPause
                      size={"12px"}
                      color={"#FFFFFF"}
                      fill={"#B12121"}
                      stroke="#B12121"
                      strokeWidth={"1.5"}
                    />
                  </span>
                  <p
                    className="payroll-label-scheduled"
                    style={{ color: "#B12121" }}
                  >
                    Paused
                  </p>
                </>
              ) : (
                <>
                  <span className="payroll-icon">
                    <FiCalendar
                      size={"12px"}
                      color={"#FFFFFF"}
                      fill={"#FFFFFF"}
                      stroke={"#0a2540"}
                      strokeWidth={"2"}
                    />
                  </span>
                  <p className="payroll-label-scheduled">Scheduled</p>
                </>
              )}
            </div>
          );
        },
      },
      {
        label: "Status",
        customComponent: (props: PayrollRow) => {
          const groupId = props.groupId as string;
          const { needsReAuth } = this._getBankAccountInfo(groupId);
          const periodState = needsReAuth
            ? "RE_AUTH_REQUIRED"
            : (props.periodState as string);
          return (
            <PayrollStatus
              periodState={periodState}
              status={props.status as string}
            />
          );
        },
      },
      {
        customComponent: (props: PayrollRow) => {
          return (
            <div data-testid="payroll-actions" className="payroll-actions">
              {this._getPayrollAction(props)}
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _getPayrollAction = (payroll: PayrollRow) => {
    const runPayrollStatuses = ["WAITING_FOR_APPROVAL", "ERROR", "CANCELED"];
    const groupId = payroll.groupId as string;
    const { bankAccountId, needsReAuth } = this._getBankAccountInfo(groupId);
    const groupHasPendingEmployee =
      this.props.employerLinkRequestsByGroup[groupId];
    const status = get(payroll, "status") as string;
    const shouldUpdateBank = includes(
      this.props.invalidBankIdsLinkedToGroups,
      bankAccountId
    );
    const readyToApprove =
      includes(runPayrollStatuses, status) && !groupHasPendingEmployee;
    const linkedEmployees = get(this.props.linkedEmployeesByGroup, groupId, []);
    const zeroLinkedEmployees = isEmpty(linkedEmployees);
    const payPeriodId = payroll.payPeriodId;
    const isProcessed = processedStatuses.includes(status);
    const isMissedPayroll = payroll.periodState === "MISSED" && !isProcessed;
    const groupHasUnprocessedLatePayroll = some(
      this.props.payrolls,
      (payroll: PayrollRow) =>
        payroll.periodState === "LATE" &&
        payroll.groupId === groupId &&
        !processedStatuses.includes(payroll.status as string)
    );
    const bankAccount = find(this.props.bankAccounts, { id: bankAccountId });
    const bankIsPending = isBankPending(bankAccount);

    const isCurrent = payroll.periodState === "CURRENT";
    const isUpcoming = payroll.periodState === "UPCOMING";
    const isWaitingForAction = status === "WAITING_FOR_ACTION";
    const isApproved = status === "APPROVED";

    let shouldDisplayAction = true;
    if (this.props.has360PayrollIntegration) {
      shouldDisplayAction = false;
    } else if (isMissedPayroll) {
      shouldDisplayAction = false;
    } else if (isCurrent && groupHasUnprocessedLatePayroll) {
      shouldDisplayAction = false;
    } else if (isUpcoming) {
      shouldDisplayAction = false;
    } else if (bankIsPending) {
      shouldDisplayAction = false;
    }

    if (isWaitingForAction) {
      return (
        <Button
          className="table-btn"
          btnLabel="Restart"
          color="action"
          name="action"
          size="sm"
          onClick={() => {
            void this.props
              .recheckPayrollBalance(
                this.props.client as ApolloClient<object>,
                payroll.payrollId as string
              )
              .then(() =>
                this.props.getAllEmployerGroups(
                  this.props.client as ApolloClient<object>
                )
              );
          }}
        />
      );
    } else if (shouldUpdateBank) {
      return (
        <Button
          className="table-btn"
          color="action"
          name="action"
          size="sm"
          btnLabel="Update Bank"
          data-testid="update-bank-button"
          onClick={() => {
            this.props.push("/dashboard/company/group");
          }}
        />
      );
    } else if (needsReAuth) {
      return (
        <Button
          name="submit"
          color="action"
          size="sm"
          onClick={() => {
            this.props.push("/dashboard/company/bank");
          }}
          btnLabel="Authenticate Bank"
          withArrow
        />
      );
    } else if (
      !this.props.has360PayrollIntegration &&
      groupHasPendingEmployee
    ) {
      return (
        <Button
          className="table-btn"
          color="action"
          name="action"
          size="sm"
          btnLabel="Review Pending Employees"
          onClick={() => this.props.push("/dashboard/company/group")}
        />
      );
    } else if (zeroLinkedEmployees && !this.props.hasPayrollIntegration) {
      return (
        <Button
          className="table-btn"
          color="action"
          name="action"
          size="sm"
          btnLabel="Invite Employees"
          onClick={() => this.props.push("/dashboard/users/employees")}
        />
      );
    } else if (readyToApprove || isMissedPayroll) {
      return (
        <>
          {shouldDisplayAction && (
            <Button
              className="table-btn"
              color="action"
              name="action"
              size="sm"
              btnLabel="Run Payroll"
              onClick={() => {
                this.props.push({
                  pathname: `/dashboard/contributions/${groupId}/approval`,
                  search: `payPeriodId=${String(payPeriodId)}`,
                });
              }}
            />
          )}
          {isMissedPayroll && !bankIsPending && (
            <>
              <span>
                <Button
                  className="table-btn"
                  color="dismiss"
                  name="action"
                  size="sm"
                  btnLabel="Dismiss"
                  onClick={() =>
                    this.setState({
                      payPeriodId: payPeriodId as string,
                      showDismissModal: true,
                      payrollData: payroll,
                    })
                  }
                />
              </span>
              {!this.props.has360PayrollIntegration &&
                payroll.adhocEligible && (
                  <span style={{ paddingLeft: 3 }}>
                    <Button
                      className="table-btn"
                      color="action"
                      name="action"
                      size="sm"
                      btnLabel="Run"
                      onClick={() => {
                        this.props.push({
                          pathname: `/dashboard/contributions/${groupId}/one-time`,
                          search: `startDate=${String(
                            payroll.payPeriodStart
                          )}&endDate=${String(
                            payroll.payPeriodEnd
                          )}&payPeriodId=${String(payPeriodId)}`,
                        });
                      }}
                    />
                  </span>
                )}
            </>
          )}
        </>
      );
    } else if (isApproved && !isEmpty(payroll.payrollId)) {
      return (
        <Button
          className="table-btn"
          name="action"
          size="sm"
          btnLabel="Cancel Payroll"
          onClick={() =>
            this._cancelPayroll(groupId, payroll.payrollId as string)
          }
          color="red"
        />
      );
    }
    return null;
  };

  _cancelPayroll = (groupId: string, payrollId: string) => {
    void this.props
      .cancelPayroll(
        this.props.client as ApolloClient<object>,
        groupId,
        payrollId
      )
      .then(() =>
        // @ts-expect-error pre-existing bug: currentPayrolls requires (client,
        // companyId) but is called with client only; preserved as-is.
        this.props.currentPayrolls(this.props.client as ApolloClient<object>)
      )
      .then((data) => {
        if (!(data as { error?: unknown }).error) {
          toast.success("Successfully canceled approved payroll.");
        }
      });
  };

  render() {
    const columns = this._buildColumns();
    const data = this._filteredPayrollGroups();

    return (
      <>
        {this.props.isFetching && <IconSpinner centered />}
        {this.state.showDismissModal && (
          <DismissPayrollModal
            // @ts-expect-error inert pre-existing prop: DismissPayrollModal
            // does not declare payPeriodId (it reads payPeriodId off
            // payrollData); passed prop is ignored, preserved as-is.
            payPeriodId={this.state.payPeriodId}
            payrollData={
              this.state.payrollData as React.ComponentProps<
                typeof DismissPayrollModal
              >["payrollData"]
            }
            onClose={() =>
              this.setState({
                showDismissModal: false,
                payrollData: null,
              })
            }
            onSuccess={() => {
              // @ts-expect-error pre-existing bug: currentPayrolls requires
              // (client, companyId) but is called with client only; preserved.
              void this.props.currentPayrolls(
                this.props.client as ApolloClient<object>
              );
              this.setState({ showDismissModal: false });
            }}
          />
        )}
        <Card className="process-contributions">
          <div className="widget-header">
            <span>
              <Card.Title>Payrolls</Card.Title>
              <Card.Text>
                <span>
                  <Form.Select
                    className="group-filter"
                    id="inlineFormCustomSelect"
                    value={this.state.selectedPayrollGroup}
                    onChange={(e) => {
                      this.setState({
                        selectedPayrollGroup: e.target.value,
                      });
                    }}
                  >
                    {map(this._payrollGroupFilter(), (group) => (
                      <option value={group.value as string} key={group.id}>
                        {group.name as React.ReactNode}
                      </option>
                    ))}
                  </Form.Select>
                </span>
              </Card.Text>
            </span>
          </div>
          {this.props.error && <Alert type="error" msg={this.props.error} />}
          <IconTable columns={columns} data={data} />
        </Card>
      </>
    );
  }
}

export default connector(withApollo<Props>(PayrollTable));
